import { ArrowRight } from 'lucide-react';
import { PreflightCopy, ErrorCode } from '@signalvote/shared';
import type { FiledReportDto } from '@signalvote/shared';
import { StagedRowUpdateError } from '@/hooks/queries';

/**
 * ADR-081 — a delete refusal as a dialog renders it: the treasurer copy, plus the
 * already-filed report to link to when the refusal names one. Structured rather than a bare
 * string because that refusal's whole job is to end on the amendment path, which needs the
 * report's id.
 */
export interface DeleteRefusal {
  message: string;
  filedReport: FiledReportDto | null;
}

/** A refusal with no report to link to — every code other than ADR-081's. */
export const plainRefusal = (message: string): DeleteRefusal => ({ message, filedReport: null });

/**
 * The ADR-081 arm shared by both delete surfaces (the cancellation chooser and the row-detail
 * confirm): the entry is on a report already sent to the regulator, so it can't be destroyed
 * whatever the filing calendar says. Returns null when this is some other failure, so each
 * caller keeps its own ordering for the rest of its codes.
 */
export function filedReportRefusal(err: unknown): DeleteRefusal | null {
  if (
    err instanceof StagedRowUpdateError
    && err.code === ErrorCode.DeleteSupportsFiledReport
    && err.filedReport
  ) {
    return {
      message: PreflightCopy.ChooserSupportsFiledReportError(err.filedReport.name),
      filedReport: err.filedReport,
    };
  }
  return null;
}

/**
 * The way out of that refusal. A plain anchor (not the router) mirrors
 * `PreviewReportButton`'s jump: a deliberate full navigation out of the dialog into the
 * report detail route, where the amendment path lives.
 */
export function FiledReportAmendLink({
  filedReport,
  testId,
}: {
  filedReport: FiledReportDto;
  testId: string;
}) {
  return (
    <a
      href={`/admin/finance/reports/${filedReport.id}`}
      data-testid={testId}
      className="mt-2 inline-flex items-center gap-1 font-medium underline"
    >
      {PreflightCopy.ChooserSupportsFiledReportAmendLink(filedReport.name)}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}
